import { Row, Col, Button, FormGroup } from "reactstrap";
import React from "react";
import ModalFrame from "../../../toolkit/modalFrame";
import {
  RequestComponent,
  RequestComponentProps,
  RequestComponentState,
  DisplayState,
} from "../../../toolkit/requestComponent";
import Reform from "@franleplant/reform";
import BaseInput from "../../../toolkit/baseInput";
import BaseSelect from "../../../toolkit/baseSelect";
import { connect } from "react-redux";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import IUser from "../../../types/services/IUser";
import { ScaleLoader } from "react-spinners";
import { firebaseConnect } from "react-redux-firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import get from "lodash.get";
import { serviceCaller } from "../../composition";

interface Recipient<T> {
  label: string;
  value: string;
  object: T;
}

interface Props extends RequestComponentProps {
  allUsers: IUser[];
}

interface State extends RequestComponentState {
  fields: {
    requestText: string;
    selectedUsers: Recipient<IUser>[];
  };
  users: Recipient<IUser>[];
  errors?: any;
  formValid: boolean;
}

class RequestContentPage extends RequestComponent<Props, State> {
  re = Reform.reactMixins.objectMixin(this);

  state = {
    displayState: "normal" as DisplayState,
    users: [],
    fields: {
      requestText: undefined,
      selectedUsers: undefined,
    },
    errors: {},
    formValid: false,
  };

  validationRules = {
    requestText: { required: true },
    selectedUsers: { required: true },
  };

  validationMessages = {
    required: _ => "Required.",
    default: _ => "Required.",
  };

  buildRecipientOptions = () => {
    return this.props.allUsers
      ? this.props.allUsers
          .filter(user => {
            let filtered =
              !user.email ||
              (!user.email.endsWith("mahisoft.com") &&
                !user.email.endsWith("viewspark.org") &&
                firebase.auth().currentUser &&
                user.id != firebase.auth().currentUser.uid);

            return filtered;
          })
          .filter(user => user.disabled == null || !user.disabled)
          .map(user => ({
            label:
              user.name + " - " + (user.email ? user.email : "Not available"),
            value: user.id,
            object: user,
          }))
      : [];
  };

  resetFormForOpen = () => {
    this.setState({
      fields: {
        requestText: "",
        selectedUsers: undefined,
      },
      displayState: "normal",
      users: this.buildRecipientOptions(),
    });
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.resetFormForOpen();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.resetFormForOpen();
    }
  }

  onMultipleUserSelectChange = options => {
    this.setState(state => {
      const value = options.length > 0 ? options : undefined;
      this.re.validateField("selectedUsers", value);
      return {
        ...state,
        fields: {
          ...state.fields,
          selectedUsers: value,
        },
      };
    });
  };

  onSubmit = () => {
    if (this.re.validateFormFromState()) {
      let recipients = {};
      this.state.fields.selectedUsers.forEach(user => {
        recipients[user.object.id] = true;
      });
      const request = {
        senderId: this.props.userId,
        message: this.state.fields.requestText,
        recipients: recipients as Map<string, boolean>,
        createdOn: new Date().getTime(),
      };
      this.submitRequest(request, false);
    }
  };

  onValidation = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(
        state => {
          const fields = {
            ...state.fields,
            [fieldName]: value,
          };
          this.re.validateField(fieldName, value);
          return { ...state, error: {}, fields };
        },
        () =>
          this.setState({
            formValid: this.re.formIsValid(),
          })
      );
    };
  };

  render() {
    return (
      <ModalFrame
        isOpen={this.props.isOpen}
        toggle={() => this.props.toggle()}
        size="xl"
        title={"Create Request"}
        className={s["modal-xl"]}
      >
        <Row className={s.marginBot}>
          <Col sm="12">
            <FormGroup>
              <label className={s.label} htmlFor="selectedUsers">
                * Recipients
              </label>
              <BaseSelect
                id="selectedUsers"
                name={"selectedUsers"}
                dataElmId={"selectedUsers"}
                className={s.select}
                isMulti
                placeholder={"Click to select recipients"}
                value={this.state.fields.selectedUsers}
                onChange={options => this.onMultipleUserSelectChange(options)}
                options={this.state.users}
                errors={this.re.mapFieldErrors("selectedUsers")}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs="12">
            <FormGroup>
              <label className={s.label} htmlFor="requestLabel">
                * Request Content
              </label>
              <BaseInput
                id={"requestText"}
                name={"requestText"}
                dataElmId={"requestText"}
                type="textarea"
                placeholder="Enter message here"
                customClassName={s.textArea}
                onChange={this.onValidation("requestText")}
                value={this.state.fields.requestText}
                errors={this.re.mapFieldErrors("requestText")}
              />
            </FormGroup>
          </Col>
        </Row>
        {this.state.displayState === "normal" && (
          <Row>
            <Col
              xs={{ size: 4, offset: 8 }}
              md={{ size: 2, offset: 10 }}
              className={s.rightText}
            >
              <Button color="none" className={s.button} onClick={this.onSubmit}>
                Submit
              </Button>
            </Col>
          </Row>
        )}
        {this.state.displayState === "executing" && (
          <Row>
            <Col
              xs={{ size: 4, offset: 8 }}
              md={{ size: 2, offset: 10 }}
              className={s.rightText}
            >
              <ScaleLoader color={"#1f65b1"} loading />
            </Col>
          </Row>
        )}
        {this.state.displayState === "success" && (
          <Row>
            <Col xs="12" className={s.rightText}>
              <p className={s.done}>Done!</p>
            </Col>
          </Row>
        )}
        {this.state.displayState === "error" && (
          <Row>
            <Col xs="12" className={s.rightText}>
              <p className={s.error}>Something went wrong, try again later.</p>
            </Col>
          </Row>
        )}
      </ModalFrame>
    );
  }
}

const mapStateToProps = state => {
  const { user, firebase } = state;
  const users: IUser[] = !get(
    firebase,
    `ordered.${user.currentCharity.id}.users`
  )
    ? undefined
    : get(firebase, `ordered.${user.currentCharity.id}.users`).map(
        userObject => {
          return {
            ...userObject.value,
            id: userObject.key,
          };
        }
      );
  return {
    userId: user.id,
    allUsers: users,
  };
};

export default connect<RequestComponentProps>(mapStateToProps)(
  serviceCaller(
    firebaseConnect((_, store) => {
      return store.getState().user.currentCharity.id !== ""
        ? [`${store.getState().user.currentCharity.id}/users`]
        : [];
    })(withStyles(s)(RequestContentPage))
  )
);
